import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";

import * as M from "../data/model";
import * as Actions from "../store/validity/actions";
import { RootState } from "../store/root";
import {
    ValidityState,
    AgentValidity,
    Validity,
    AttendantsValidity,
    CustomerValidity,
    ProductPickerValidity,
    OccasionValidity,
    GlobalOccasionValidity,
    GlobalOccasionSectionValidity,
    GlobalOccasionEntryValidity,
    DivisionValidity,
    AnalysisValidity,
    DemandValidity,
    RecommendationValidity,
    DecisionValidity,
    PriorInsuranceValidity,
    ValidityMap,
    LineValidity,
    DocumentValidity,
} from "../store/validity/state";
import { isIndividual, isCompany } from "../data/model-utils";
import { relink } from "../utils/utils";

import {
    ValidatorComposer,
    combineValidities,
    combineValidityMap,
    notEmpty,
    integer,
    on,
    off,
    emptyValidity,
    notNull,
    manualCheck,
    strings,
    combineValidity,
} from "./validators";
import * as V from "./validators";
import { filter } from "rxjs";

@Injectable()
export class ValidationService {
    // TODO: This shpuld be a producer
    private lastValidity: ValidityState = new ValidityState();

    private check: ValidatorComposer<any>;

    constructor(private store: Store<RootState>) {
        this.store
            .select((s) => s.model.ConsultationProcess)
            .pipe(filter((_) => !!_))
            .subscribe((cp) => {
                this.check = new ValidatorComposer(
                    (v) => new Validity(true, null, false, cp.IsFinished),
                );
                const newValidity = this.validate(cp);
                this.store.dispatch(new Actions.Update(newValidity));
            });
    }

    protected validate(cp: M.ConsultationProcess): ValidityState {
        let newValidity = this.validateConsultationProcess(cp);
        newValidity = relink(newValidity, this.lastValidity);
        this.lastValidity = newValidity;
        return newValidity;
    }

    protected validateConsultationProcess(
        cp: M.ConsultationProcess,
    ): ValidityState {
        const agentValidity = this.validateAgent(cp);
        const attendantsValidity = this.validateAttendants(cp);
        const customerValidity = this.validateCustomer(cp);
        const productPickerValidity = this.validateProductPicker(cp);
        const occasionValidity = this.validateOccasion(cp);
        const analysisValidity = this.validateAnalysis(cp);
        const documentValidity = this.validateDocument(cp);

        const validity = combineValidities([
            agentValidity,
            attendantsValidity,
            customerValidity,
            productPickerValidity,
            occasionValidity,
            analysisValidity,
            documentValidity,
        ]);

        return {
            ...validity,
            agent: agentValidity,
            attendants: attendantsValidity,
            customer: customerValidity,
            productPicker: productPickerValidity,
            occasion: occasionValidity,
            analysis: analysisValidity,
            document: documentValidity,
        };
    }

    protected validateAgent(cp: M.ConsultationProcess): AgentValidity {
        const agent = cp.Agent;

        const validStr = (s) => !!(s && s.trim());

        const mandant = this.check.is(notEmpty).run(cp.TenantType);
        const agenturNummer = this.check.is(notEmpty).run(agent.AgencyNumber);
        const agenturName = this.check
            .is(notEmpty)
            .onlyIf((_) => !validStr(agent.LastName) && cp.TenantType !== "DB")
            .run(agent.AgencyName);
        const vorname = new Validity(true, null, false, false);
        const nachname = this.check
            .is(notEmpty)
            .onlyIf((_) => !validStr(agent.AgencyName))
            .run(agent.LastName);
        const vorlageVermittlerprofil = this.check
            .is(on)
            .onlyIf((_) => cp.TenantType !== "DA")
            .run(cp.IsProfileHanded);

        const validity = combineValidities([
            mandant,
            agenturNummer,
            agenturName,
            vorname,
            nachname,
            vorlageVermittlerprofil,
        ]);

        return {
            ...validity,
            mandant: mandant,
            agenturNummer: agenturNummer,
            agenturName: agenturName,
            vorname: vorname,
            nachname: nachname,
            vorlageVermittlerprofil: vorlageVermittlerprofil,
        };
    }

    protected validateAttendants(
        cp: M.ConsultationProcess,
    ): AttendantsValidity {
        const protocol = cp.Protocol;
        const customer = cp.Customer;

        const creatorName = this.check.is(notEmpty).run(protocol.CreatorName);
        const customerName = this.check.is(notEmpty).run(
            (() => {
                if (isIndividual(customer)) {
                    return customer.LastName;
                } else if (isCompany(customer)) {
                    return customer.Name;
                } else {
                    return undefined;
                }
            })(),
        );
        const salesType = this.check.is(notEmpty).run(cp.SalesType);
        const consultationDates = this.check
            .is(notEmpty)
            .run(cp.ConsultationDates);

        const participantItems = {};
        protocol.Participants.forEach((participant: M.Participant) => {
            participantItems[participant.Id] = this.check
                .is(notEmpty)
                .run(participant.Name);
        });

        const consultationDateItems = {};
        cp.ConsultationDates.forEach((consultationDate: M.ConsultationDate) => {
            consultationDateItems[consultationDate.Id] = this.check
                .is(notEmpty)
                .run(consultationDate.Date);
        });

        const validity = combineValidities([
            creatorName,
            customerName,
            salesType,
            consultationDates,
            combineValidityMap(participantItems),
            combineValidityMap(consultationDateItems),
        ]);

        return {
            ...validity,
            creatorName: creatorName,
            customerName: customerName,
            salesType: salesType,
            consultationDates: consultationDates,
            participantItems: participantItems,
            consultationDateItems: consultationDateItems,
        };
    }

    protected validateCustomer(cp: M.ConsultationProcess): CustomerValidity {
        const customer = cp.Customer;

        const individual = (v: any) => isIndividual(customer);
        const company = (v: any) => isCompany(customer);

        const individualSalutation = this.ifIndividual(customer, "Salutation");
        const birthdate = this.ifIndividualDate(customer, "Birthdate");
        const firstName = this.ifIndividual(customer, "FirstName");
        const lastName = this.ifIndividual(customer, "LastName");

        const companySalutation = this.ifCompany(customer, "Salutation");
        const companyName = this.ifCompany(customer, "Name");
        const legalForm = this.ifCompany(customer, "LegalForm");
        const contactSalutation = this.ifCompany(customer, "ContactSalutation");
        const contactName = this.ifCompany(customer, "ContactName");

        const country = this.check.is(notEmpty).run(customer.Address.Country);
        const zip = this.check.is(notEmpty).run(customer.Address.Zip);
        const city = this.check.is(notEmpty).run(customer.Address.City);
        const street = this.check
            .is(notEmpty)
            .onlyIf(() => !customer.Address.IsPostOfficeBox)
            .run(customer.Address.Street);
        const streetNumber = this.check
            .is(notEmpty)
            .onlyIf(() => !customer.Address.IsPostOfficeBox)
            .run(customer.Address.StreetNumber);
        const postOfficeBoxNumber = this.check
            .is(notEmpty)
            .onlyIf(() => customer.Address.IsPostOfficeBox)
            .run(customer.Address.PostOfficeBoxNumber);

        const validity = combineValidities([
            individualSalutation,
            birthdate,
            firstName,
            lastName,
            companySalutation,
            companyName,
            legalForm,
            contactSalutation,
            contactName,
            country,
            zip,
            city,
            street,
            streetNumber,
            postOfficeBoxNumber,
        ]);

        return {
            ...validity,

            individualSalutation: individualSalutation,
            birthdate: birthdate,
            firstName: firstName,
            lastName: lastName,

            companySalutation: companySalutation,
            companyName: companyName,
            legalForm: legalForm,
            contactSalutation: contactSalutation,
            contactName: contactName,

            country: country,
            zip: zip,
            city: city,
            street: street,
            streetNumber: streetNumber,
            postOfficeBoxNumber: postOfficeBoxNumber,
        };
    }

    protected ifIndividual(customer: M.Customer, prop: keyof M.Individual) {
        const individual = customer as M.Individual;
        return this.check
            .is(notEmpty)
            .onlyIf((_) => isIndividual(customer))
            .run(individual[prop]);
    }
    protected ifIndividualDate(customer: M.Customer, prop: keyof M.Individual) {
        const individual = customer as M.Individual;
        return this.check
            .is(V.validDate)
            .onlyIf((_) => isIndividual(customer))
            .run(individual[prop]);
    }
    protected ifCompany(customer: M.Customer, prop: keyof M.Company) {
        const company = customer as M.Company;
        return this.check
            .is(notEmpty)
            .onlyIf((_) => isCompany(customer))
            .run(company[prop]);
    }

    protected validateProductPicker(
        cp: M.ConsultationProcess,
    ): ProductPickerValidity {
        const hasProducts = cp.Protocol.Offers.some(
            (offer) =>
                offer.Divisions.length > 0 || offer.ChangeType !== "ADDITION",
        );
        const products = this.check.is(on).run(hasProducts);

        const validity = combineValidities([products]);

        return {
            ...validity,
        };
    }

    protected validateOccasion(cp: M.ConsultationProcess): OccasionValidity {
        const protocol = cp.Protocol;

        const eventEntries = {};
        protocol.OccasionOccasionEntries.forEach(
            (entry: M.OccasionOccasionEntry) => {
                eventEntries[entry.Id] = this.check
                    .is(notEmpty)
                    .onlyIf((_) => entry.ConsultingRequested)
                    .run(entry.OccasionName);
            },
        );

        const global = this.validateGlobalOccasion(protocol.GlobalOccasion);

        let validity: Validity;
        switch (cp.Protocol.Occasion) {
            case "PRODUCT":
                validity = emptyValidity;
                break;
            case "OCCASION":
                validity = combineValidityMap(eventEntries);
                break;
            case "GLOBAL":
                validity = global;
                break;
        }

        return {
            ...validity,
            eventEntries: eventEntries,
            global: global,
        };
    }

    protected validateGlobalOccasion(
        globalOccasion: M.GlobalOccasion,
    ): GlobalOccasionValidity {
        const consultationDates = {};
        globalOccasion.ConsultationDates.forEach(
            (consultationDate: M.ConsultationDate) => {
                consultationDates[consultationDate.Id] = this.check
                    .is(notEmpty)
                    .run(consultationDate.Date);
            },
        );

        const sections = {};
        globalOccasion.GlobalOccasionSections.forEach(
            (section: M.GlobalOccasionSection) => {
                sections[section.Id] =
                    this.validateGlobalOccasionSection(section);
            },
        );

        const validity = combineValidities([
            combineValidityMap(consultationDates),
            combineValidityMap(sections),
        ]);

        return {
            ...validity,
            consultationDates: consultationDates,
            sections: sections,
        };
    }

    protected validateGlobalOccasionSection(
        section: M.GlobalOccasionSection,
    ): GlobalOccasionSectionValidity {
        const entries = {};
        section.GlobalOccasionEntries.forEach(
            (entry: M.GlobalOccasionEntry) => {
                entries[entry.Id] = this.validateGlobalOccasionEntry(entry);
            },
        );

        const validity = combineValidities([combineValidityMap(entries)]);

        return {
            ...validity,
            entries: entries,
        };
    }

    protected validateGlobalOccasionEntry(
        entry: M.GlobalOccasionEntry,
    ): GlobalOccasionEntryValidity {
        const priority = this.check.is(notNull).run(entry.Priority);
        const consultingRequested = this.check
            .is(notNull)
            .run(entry.ConsultingRequested);

        const validity = combineValidities([priority, consultingRequested]);

        return {
            ...validity,
            priority: priority,
            consultingRequested: consultingRequested,
        };
    }

    protected validateAnalysis(cp: M.ConsultationProcess): AnalysisValidity {
        const protocol = cp.Protocol;

        const divisions = {};
        protocol.Offers.forEach((offer) => {
            offer.Divisions.forEach((division) => {
                divisions[division.Id] = this.validateDivision(cp, division);
            });
        });

        const lines: ValidityMap<LineValidity> = {};
        const lineDemands = {};
        const lineDivisions = {};
        const demands = {};
        const seenProducts = {};
        for (const offer of protocol.Offers) {
            for (const div of offer.Divisions) {
                const line = div.GroupKey;
                if (lineDivisions[line]) {
                    lineDivisions[line].push(divisions[div.Id]);
                } else {
                    lineDivisions[line] = [divisions[div.Id]];
                    lineDemands[line] = [];
                }
                if (!seenProducts[div.ProductKey]) {
                    seenProducts[div.ProductKey] = true;
                    const demandValidity = this.validateDemand(div.Demand);
                    demands[div.Id] = demandValidity;
                    lineDemands[line].push(demandValidity);
                }
            }
        }
        Object.keys(lineDivisions).forEach((line) => {
            lines[line] = this.validateLine(
                lineDemands[line],
                lineDivisions[line],
            );
        });

        const validity = combineValidities([
            combineValidityMap(demands),
            combineValidityMap(divisions),
            // derived validities not needed: lines
        ]);

        return {
            ...validity,
            demands: demands,
            divisions: divisions,
            lines: lines,
        };
    }

    protected validateLine(
        demands: DemandValidity[],
        divisions: DivisionValidity[],
    ): LineValidity {
        const recommendations = divisions.map((div) => div.recommendation);
        const decisions = divisions.map((div) => div.decision);

        const demand = combineValidities(demands);
        const recommendation = combineValidities(recommendations);
        const decision = combineValidities(decisions);

        const validity = combineValidities([demand, recommendation, decision]);

        return {
            ...validity,
            demand: demand,
            recommendation: recommendation,
            decision: decision,
        };
    }

    protected validateDivision(
        cp: M.ConsultationProcess,
        division: M.Division,
    ): DivisionValidity {
        const risk = emptyValidity;
        const recommendation = this.validateRecommendation(
            division.Recommendation,
        );
        const decision = this.validateDecision(cp, division);

        const validity = combineValidities([risk, recommendation, decision]);

        return {
            ...validity,
            risk: risk,
            recommendation: recommendation,
            decision: decision,
        };
    }

    protected validateDemand(demand: M.Demand): DemandValidity {
        const description = this.validateTrackingText(demand.Description);

        const validity = combineValidities([description]);

        return {
            ...validity,
            description: description,
        };
    }

    protected validateRecommendation(
        recommendation: M.Recommendation,
    ): RecommendationValidity {
        const description = this.validateTrackingText(
            recommendation.Description,
        );
        const reason = this.validateTrackingText(recommendation.Reason);

        const cancellations = {};
        recommendation.RecommendedCancellations.forEach((cancellation) => {
            cancellations[cancellation.Id] =
                this.validateCancellation(cancellation);
        });
        let cancellationsEmpty = emptyValidity;
        let cancellationImpacts = emptyValidity;
        if (recommendation.IsCancellationRecommended) {
            cancellationsEmpty = this.check
                .is(notEmpty)
                .run(recommendation.RecommendedCancellations);
            cancellationImpacts = this.validateTrackingText(
                recommendation.CancellationImpacts,
            );
        }

        const exemptions = {};
        recommendation.RecommendedExemptions.forEach((exemption) => {
            exemptions[exemption.Id] = this.validateExemption(exemption);
        });
        let exemptionsEmpty = emptyValidity;
        let exemptionImpacts = emptyValidity;
        if (recommendation.IsExemptionRecommended) {
            exemptionsEmpty = this.check
                .is(notEmpty)
                .run(recommendation.RecommendedExemptions);
            exemptionImpacts = this.validateTrackingText(
                recommendation.ExemptionImpacts,
            );
        }

        const priorInsurances = combineValidities([
            combineValidityMap(cancellations),
            cancellationsEmpty,
            cancellationImpacts,
            combineValidityMap(exemptions),
            exemptionsEmpty,
            exemptionImpacts,
        ]);

        const validity = combineValidities([
            description,
            reason,
            priorInsurances,
        ]);

        return {
            ...validity,
            description: description,
            reason: reason,
            cancellations: cancellations,
            cancellationImpacts: cancellationImpacts,
            exemptions: exemptions,
            exemptionImpacts: exemptionImpacts,
            priorInsurances: priorInsurances,
        };
    }

    protected validateCancellation(
        cancellation: M.Cancellation,
    ): PriorInsuranceValidity {
        const company = this.check
            .is(notEmpty)
            .run(cancellation.CancellationCompany);
        const policyNumber = this.check
            .is(notEmpty)
            .run(cancellation.CancellationInsuranceNumber);

        const validity = combineValidities([company, policyNumber]);

        return {
            ...validity,
            company: company,
            policyNumber: policyNumber,
        };
    }

    protected validateExemption(
        exemption: M.Exemption,
    ): PriorInsuranceValidity {
        const company = this.check.is(notEmpty).run(exemption.ExemptionCompany);
        const policyNumber = this.check
            .is(notEmpty)
            .run(exemption.ExemptionInsuranceNumber);

        const validity = combineValidities([company, policyNumber]);

        return {
            ...validity,
            company: company,
            policyNumber: policyNumber,
        };
    }

    protected validateDecision(
        cp: M.ConsultationProcess,
        division: M.Division,
    ): DecisionValidity {
        const decision = division.Decision;

        const reasonRequired = division.Application === "WebLife";
        const decisionRequired = ["ZAS", "SME"].includes(division.Application);
        const notRecommendedRequired =
            !decision.IsDecisionSameAsRecommendation &&
            division.Application !== "WebLife";

        const description = this.validateTrackingText(decision.Description);
        const reason = this.validateTrackingText(
            decision.Reason,
            reasonRequired,
        );
        const isAccepted = this.check
            .is(notNull)
            .onlyIf((_) => decisionRequired && !division.ViaLink)
            .run(decision.IsAccepted);
        const asRecommendedReason = this.check
            .is(notEmpty)
            .onlyIf((_) => notRecommendedRequired)
            .run(decision.AsRecommendedReason);
        const rejectReason = this.check
            .is(notEmpty)
            .onlyIf((_) => !division.ViaLink && decision.IsAccepted === false)
            .run(decision.RejectReason);

        const validity = combineValidities([
            description,
            reason,
            isAccepted,
            asRecommendedReason,
            rejectReason,
        ]);

        return {
            ...validity,
            description: description,
            reason: reason,
            isAccepted: isAccepted,
            asRecommendedReason: asRecommendedReason,
            rejectReason: rejectReason,
        };
    }

    protected validateTrackingText(
        trackingText: M.TrackingText,
        required = true,
    ): Validity {
        const notEmpty =
            (trackingText.MainText != null &&
                trackingText.MainText.length > 0) ||
            (trackingText.AdditionalText != null &&
                trackingText.AdditionalText.length > 0);
        const contentValid =
            trackingText.MainText == null ||
            (!trackingText.MainText.includes("...") &&
                !trackingText.MainText.includes("…"));
        const isValid = contentValid && (notEmpty || !required);
        return manualCheck(strings.required, required, isValid);
    }

    protected validateDocument(cp: M.ConsultationProcess): DocumentValidity {
        const finished = this.check.is(on).run(cp.IsFinished);

        const validity = combineValidities([finished]);

        return {
            ...validity,
            finished: finished,
        };
    }
}
